import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  FormControlLabel,
  Switch,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useNavigate } from "react-router-dom";
import deviceStore from "../../stores/DeviceStore";
import deviceTypeStore from "../../stores/DeviceTypeStore";
import locationStore from "../../stores/LocationStore";
import regionStore from "../../stores/RegionStore";
import AddressComponent from "../AddressComponent";
import { observer } from "mobx-react-lite";
import { useTheme } from "@mui/material/styles";

const DeviceForm = observer(() => {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [device, setDevice] = useState(null);
  const [formDevice, setFormDevice] = useState({
    name: "",
    devicePimiosId: "",
    typeId: "",
    locationId: "",
    regionId: "",
    isActive: true,
    status: "connected",
    imageUrl: "",
    newLocation: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "USA",
      latitude: "",
      longitude: "",
    },
  });

  const [useNewLocation, setUseNewLocation] = useState(false);

  useEffect(() => {
    deviceTypeStore.fetchDeviceTypes();
    locationStore.fetchLocations();
    regionStore.fetchRegions();

    if (id !== "new") {
      if (
        deviceStore.selectedDevice &&
        deviceStore.selectedDevice.id === Number(id)
      ) {
        const selected = deviceStore.selectedDevice;
        setDevice(selected);
        setFormDevice({
          name: selected.name || "",
          devicePimiosId: selected.pimios_id || "",
          typeId: selected.type_id || "",
          locationId: selected.location_id || "",
          regionId: selected.region_id || "",
          isActive: selected.status === "connected",
          status: selected.status || "connected",
          imageUrl: selected.image_url || "",
          newLocation: {
            street: "",
            city: "",
            state: "",
            postalCode: "",
            country: "USA",
            latitude: "",
            longitude: "",
          },
        });
      } else {
        deviceStore.loadDeviceById(id).then((data) => {
          if (data) {
            setDevice(data);
            setFormDevice({
              name: data.name || "",
              devicePimiosId: data.pimios_id || "",
              typeId: data.type_id || "",
              locationId: data.location_id || "",
              regionId: data.region_id || "",
              isActive: data.status === "connected",
              status: data.status || "connected",
              imageUrl: data.image_url || "",
              newLocation: {
                street: "",
                city: "",
                state: "",
                postalCode: "",
                country: "USA",
                latitude: "",
                longitude: "",
              },
            });
          }
        });
      }
    }
  }, [id]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormDevice((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleNewLocationChange = (event) => {
    const { name, value } = event.target;
    setFormDevice((prev) => ({
      ...prev,
      newLocation: { ...prev.newLocation, [name]: value },
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const missingFields = [];
    if (!formDevice.devicePimiosId) missingFields.push("Device Pimios ID");
    if (!formDevice.typeId) missingFields.push("Device Type");
    if (!useNewLocation && !formDevice.locationId)
      missingFields.push("Location");
    if (!formDevice.regionId) missingFields.push("Region");

    if (useNewLocation) {
      const { street, city, state, postalCode, country } =
        formDevice.newLocation;
      if (!street) missingFields.push("Street");
      if (!city) missingFields.push("City");
      if (!state) missingFields.push("State");
      if (!postalCode) missingFields.push("Postal Code");
      if (!country) missingFields.push("Country");
    }

    if (missingFields.length > 0) {
      toast.error(`Missing required fields: ${missingFields.join(", ")}`);
      return;
    }

    const statusMapping = {
      connected: 1,
      disconnected: 0,
      maintenance: 2,
    };

    let locationId = formDevice.locationId;

    if (useNewLocation) {
      const newLocationId = await locationStore.createLocation(
        formDevice.newLocation
      );
      locationId = newLocationId;
    }

    const deviceData = {
      name: formDevice.name,
      pimios_id: formDevice.devicePimiosId,
      type_id: formDevice.typeId,
      location_id: locationId,
      region_id: formDevice.regionId,
      status: statusMapping[formDevice.status] || 0,
      image_url: formDevice.imageUrl,
    };

    try {
      if (id === "new") {
        await deviceStore.createDevice(deviceData);
        toast.success("Device created successfully!");
      } else {
        await deviceStore.updateDevice(id, deviceData);
        toast.success("Device updated successfully!");
      }
      navigate("/devices");
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("No user data found!");
      } else if (error.response?.status === 403) {
        toast.error("Forbidden, insufficient permissions.");
      } else if (error.response?.status === 409) {
        toast.error("A device with this name already exists.");
      } else {
        toast.error("Error saving device. Please try again.");
      }
    }
  };

  return (
    <Box
      className="p-6 overflow-y-auto"
      sx={{
        backgroundColor: isDarkMode
          ? theme.palette.background.default
          : "white",
        minHeight: "100vh",
      }}
    >
      <Box className="mb-4 flex items-center">
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ color: theme.palette.text.primary }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
          {id === "new"
            ? "Add New Device"
            : `Edit Device${device?.name ? ` – ${device.name}` : ""}`}
        </Typography>
      </Box>

      <Paper
        elevation={3}
        className="p-6 mx-auto max-w-3xl rounded-lg"
        sx={{
          backgroundColor: isDarkMode
            ? theme.palette.background.paper
            : "white",
          color: theme.palette.text.primary,
        }}
      >
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Device Name"
            name="name"
            value={formDevice.name}
            onChange={handleChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Device Pimios ID"
            name="devicePimiosId"
            value={formDevice.devicePimiosId}
            onChange={handleChange}
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Device Type</InputLabel>
            <Select
              name="typeId"
              value={formDevice.typeId}
              onChange={handleChange}
              required
            >
              {deviceTypeStore.deviceTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.manufacture} - {type.type_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Region</InputLabel>
            <Select
              name="regionId"
              value={formDevice.regionId}
              onChange={handleChange}
              required
            >
              {regionStore.regions.map((region) => (
                <MenuItem key={region.id} value={region.id}>
                  {region.name} - {region.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Location</InputLabel>
            <Select
              name="locationId"
              value={formDevice.locationId}
              onChange={handleChange}
              disabled={useNewLocation}
              required
            >
              {locationStore.locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  {loc.street}, {loc.city}, {loc.state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={useNewLocation}
                onChange={() => setUseNewLocation(!useNewLocation)}
              />
            }
            label="Add New Location"
          />

          {useNewLocation && (
            <AddressComponent
              address={formDevice.newLocation}
              handleChange={handleNewLocationChange}
              required
            />
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formDevice.status}
              onChange={handleChange}
            >
              <MenuItem value="connected">Connected</MenuItem>
              <MenuItem value="disconnected">Disconnected</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formDevice.isActive}
                onChange={handleChange}
                name="isActive"
              />
            }
            label="Active"
          />

          <Box sx={{ display: "flex", flexDirection: "column", mt: 2, mb: 2 }}>
            {id !== "new" && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => deviceStore.deleteDevice(id)}
                sx={{ mt: 2 }}
              >
                Delete Device
              </Button>
            )}
            <Button
              variant="contained"
              type="submit"
              sx={{
                mt: 2,
                backgroundColor: isDarkMode ? "#50fa7b" : "#22C55E",
              }}
            >
              {id === "new" ? "Create Device" : "Update Device"}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
});

export default DeviceForm;
